import { AppButton } from 'components';
import Editor from 'components/CKEditor/Editor';
import DOMPurify from 'dompurify';
import useManageTopicNotes from 'hooks/API/useManageTopicNotes';
import useToaster from 'hooks/useToaster';
import Joi from 'joi';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReviewContent from './../ReviewContent';
import AppBlocks from './Blocks/AppBlocks';
import { getRandomUid, isValidUuid } from 'utilities/uuid.service';
import { BsPencil } from 'react-icons/bs';
import { FcOk } from 'react-icons/fc';
import { IoIosEye } from 'react-icons/io';
import { FaTimes } from 'react-icons/fa';
// import { TiPlus } from 'react-icons/ti';
// import { HiOutlineMinusCircle } from 'react-icons/hi';
import UploadBackDrop from './Blocks/UploadBackDrop';
import EquationBox from './Blocks/EquationBox';
import * as AppPics from 'assets/images';
import { EditorPreviewContext } from 'context/EditorPreviewContext';
import { ROLES } from 'utilities/constants';
// import { AiOutlineInfoCircle } from 'react-icons/ai';
// import { IconButton, Tooltip } from '@mui/material';
import { AuthContext } from 'context/AuthContext';
import useDebounce from 'hooks/useDebounce';
import SetTime from '../SetTime';
import UserSelect from 'components/molecules/UserSelect';
import TutorRating from 'components/TutorRating';
// import RearrangeBlocks from '../RearrangeBlocks';
import RatingUpdateModal from 'pages/manage-quiz/manage/Questions/RatingUpdateModal';
import { MdOutlinePreview } from 'react-icons/md';
import { ImEnlarge2 } from 'react-icons/im';
import ImagePreview from './ImagePreview';

export const blockMasterSchema = {
  id: '',
  value: '',
  htmlValue: '',
  assetUri: '',
  assetUrl: '',
  currentBlock: '',
  caption: '',
  captionDisabled: true,
};

const RATE_UPDATE_CONFIG = {
  show: false,
  data: {},
};

function TopicNotesTab({ data, subtopicID, userContext = {} }) {
  const { PreviewBtn } = useContext(EditorPreviewContext);
  const { classID, courseID, chapterID, topicID } = useParams();
  const { userPermissions = {}, userInfo = {} } = userContext;
  const { authState } = useContext(AuthContext);
  const token = authState?.token;
  const [openUploadModal, toggleUploadModal] = useState({ currentBlock: '', block: null });
  const [reOrderBlocks, setReOrderBlocks] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [status, setStatus] = useState('inactive');
  const [formErrors, setFormErrors] = useState([]);
  const [setTime, handleSetTime] = useState({ timeLabel: 'Min', time: 0 });
  const [imgPreviewModal, setImgPreviewModal] = useState({ isOpen: false, data: {} });

  const [updatingRatingModal, setUpdatingRatingModal] = useState(
    Object.assign({}, RATE_UPDATE_CONFIG)
  );

  const [contentOwner, setContentOwner] = useState(null);
  const [contentMaintainer, setContentMaintainer] = useState(null);
  const [contentTutor, setContentTutor] = useState(null);
  const [rating, setRating] = useState(null);

  const isTeacher = userInfo?.data?.key === ROLES.TUTOR;
  const isSE = userInfo?.data?.key === ROLES.SUBJECT_EXPERT;
  const isAdmin = userInfo?.data?.key === ROLES.ADMIN;

  const { mutate, isLoading, isError } = useManageTopicNotes({ topicID });
  useToaster(isLoading, { type: isError ? 'error' : 'success' });

  useEffect(() => {
    if (data?.notes) {
      setNotes(data.notes);
    }

    return () => {};
  }, [data?.notes]);

  useEffect(() => {
    if (notes[0]?.blocks) {
      const __blocks = notes[0]?.blocks
        ?.map((x, i) => ({
          ...x,
          id: x._id,
          editBlock: false,
          sortPosition: x.sortPosition ?? i,
        }))
        .sort((a, b) => a.sortPosition > b.sortPosition);
      setBlocks(__blocks);
      setReOrderBlocks(__blocks);
    }
    if (notes[0]?.status) setStatus(notes[0]?.status);
    if (notes[0]?.readTime)
      handleSetTime((x) => ({ ...notes[0]?.readTime, ...(notes[0]?.readTime || {}) }));
    if (notes[0]?.contentOwner) setContentOwner(notes[0]?.contentOwner);
    if (notes[0]?.assignedTutorForReview) setContentTutor(notes[0]?.assignedTutorForReview);
    if (notes[0]?.contentMaintainedBy) setContentMaintainer(notes[0]?.contentMaintainedBy);

    if (notes[0]?.ratings?.currentRating) setRating(notes[0]?.ratings?.currentRating);
  }, [notes]);

  function handleSave(args = {}, latest = {}) {
    if (isLoading) return;

    const noteID = notes[0]?._id || notes[0]?.id;
    const plans = notes[0]?.plans;

    const data = {
      status,
      ...args,
      title: 'Notes',
      _id: noteID,
      noteID,
      plans,
      blocks: serializeNoteBlocks(blocks),
      contentOwner: contentOwner?._id,
      contentMaintainedBy: contentMaintainer?._id,
      assignedTutorForReview: contentTutor?._id,
      ...latest,
    };
    console.log('🚀 ~ handleSave ~ data:', data);

    const validation = manageNotesSchema.validate(data, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    const apiData = {
      notes: [data],
      setTime,
      subtopicID,
      type: noteID ? 'UPDATE' : 'CREATE',
      reOrderingQuotient: latest?.reOrderingQuotient,
    };
    // return
    mutate(apiData, {
      onSuccess() {
        latest?.cb?.();
      },
    });
  }

  function BlockEditBtn({ block, enableEdit }) {
    return (
      <AppButton
        variantClass="btn btn-md btn-danger mx-4"
        onClick={() => enableEdit(block.id, block.editBlock)}
      >
        {!block.editBlock && (
          <>
            <BsPencil />
            Edit Block
          </>
        )}
        {block.editBlock && (
          <>
            <FcOk />
            Done Editing
          </>
        )}
      </AppButton>
    );
  }

  function BlockDeleteBtn({ block }) {
    return (
      <AppButton
        variantClass="btn btn-md btn-danger mx-4"
        onClick={() => setBlocks(blocks.filter((x) => x.id !== block.id))}
      >
        <FaTimes />
        Delete Block
      </AppButton>
    );
  }

  function updateBlock(obj = {}, block) {
    // If no type is selected; then do nothing.
    if (!obj.currentBlock) return;

    setBlocks((b) =>
      b.map((x) => {
        if (x.id === block.id) {
          return { ...x, ...obj };
        }
        return x;
      })
    );
  }

  function closeUploadModal() {
    toggleUploadModal({ currentBlock: '', block: null });
  }
  function enableEditing(id, currentStatus) {
    let currentBlocks = blocks;
    currentBlocks = currentBlocks.map((m) => {
      id === m.id ? (m.editBlock = !currentStatus) : (m.editBlock = false);
      return m;
    });
    setBlocks(currentBlocks);
    if (currentStatus) updateCurrentEditBlock({});
  }

  const __linkParams = { lessonType: 'notes' };
  if (subtopicID) __linkParams.subtopic = subtopicID;

  const blockTypeExists = () =>
    notes[0]?.blocks?.some(
      (block) => !['video', 'image', 'file', 'audio'].includes(block.currentBlock)
    );

  const respondentHasComments = () => notes[0]?.viewCommentButton;
  const canChangeStatus = () =>
    typeof notes[0]?.changeStatus === 'boolean' ? notes[0]?.changeStatus : true;

  const canSave = (bypass = '') => {
    if (isTeacher && bypass === '') return false;
    return typeof notes[0]?.canSave === 'boolean' ? notes[0]?.canSave : true;
  };

  const commentsAvailable = () => {
    const { data = { key: '' } } = userInfo;
    if (
      `${data?.key}`.toLowerCase() === 'SUBJECT_EXPERT'.toLowerCase() &&
      notes[0].status === 'review'
    ) {
      return true;
    }
    if (
      `${data?.key}`.toLowerCase() === 'TUTOR'.toLowerCase() &&
      notes[0].status === 'teacher_review'
    ) {
      return true;
    }
    if (`${data?.key}`.toLowerCase() === 'QC'.toLowerCase()) {
      return true;
    }
    if (`${data?.key}`.toLowerCase() === 'ADMIN'.toLowerCase()) {
      return true;
    }
    return false;
  };

  const canMarkComments = blockTypeExists() && commentsAvailable() && respondentHasComments();
  const [currentEdited, updateCurrentEditBlock] = useState({});

  const editorData = useDebounce(currentEdited, 100);

  useEffect(() => {
    if (editorData.id) {
      updateCurrentEditBlock(editorData);
      setBlocks(
        blocks.map((x) => {
          if (x.id === editorData.id) return { ...x, value: editorData.data };
          return x;
        })
      );
    }
  }, [editorData]);

  const teacherCannotChangeStatus =
    isTeacher && !rating && canChangeStatus() && status === 'teacher_review';

  console.log('URL:', process.env.REACT_APP_ADD_VIEWCOMMENTS_BASE_URL);

  return (
    <>
      {imgPreviewModal.isOpen && (
        <ImagePreview
          open
          data={imgPreviewModal.data}
          closeModal={() => {
            setImgPreviewModal({ isOpen: false, data: {} });
          }}
        />
      )}
      {updatingRatingModal.show && (
        <RatingUpdateModal
          show={updatingRatingModal.show}
          closeModal={() => {
            setUpdatingRatingModal(Object.assign({}, RATE_UPDATE_CONFIG));
          }}
          data={updatingRatingModal.data}
        />
      )}
      <div className="container ninty_percent_width p-0 relative">
        {openUploadModal.currentBlock && (
          <UploadBackDrop
            data={{ openUploadModal }}
            closeModal={() => {
              closeUploadModal();
            }}
            handleSave={(obj, block) => {
              if (block) {
                updateBlock(obj, block);
                closeUploadModal();
                return;
              }
              setBlocks(
                blocks.concat({
                  ...blockMasterSchema,
                  id: getRandomUid(),
                  ...obj,
                })
              );
              closeUploadModal();
            }}
          />
        )}
        <div className="row">
          <div className="col-12 publishheader">
            <h2 className="prime-head p-0 boldtitle">Notes</h2>
            <div className="row  mb-4 btnsubmitmain">
              <div className="btnTab-foot">
                {userPermissions?.topic?.notes?.canPublish && (
                  <button
                    type="button"
                    className="btn-right-content next"
                    onClick={() => {
                      setStatus('active');
                      handleSave({
                        status: 'active',
                        isPublished: !notes[0]?.isPublished ?? false,
                      });
                    }}
                  >
                    {!notes[0]?.isPublished ? 'Publish Contents' : 'Unpublish'}
                  </button>
                )}
              </div>
              &nbsp;&nbsp;
              {canMarkComments &&
                (userPermissions?.topic?.notes?.canAddComments ||
                  userPermissions?.topic?.notes?.canViewComments) && (
                  <>
                    {process.env.REACT_APP_WETTING_VERSION === 'v2' ? (
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-right-content next"
                        href={`${
                          process.env.REACT_APP_ADD_VIEWCOMMENTS_BASE_URL
                        }?class=${classID}&course=${courseID}&chapter=${chapterID}&topic=${topicID}&token=${token}&${new URLSearchParams(
                          __linkParams
                        ).toString()}`}
                      >
                        {`${userInfo?.data?.key}`.toLowerCase() === 'SUBJECT_EXPERT'.toLowerCase()
                          ? 'Respond/View comments'
                          : 'Add/View comments'}
                      </a>
                    ) : (
                      <Link
                        target={notes[0].status === 'review' ? '_blank' : ''}
                        className="btn-right-content next"
                        to={`/classes/${classID}/courses/${courseID}/chapters/${chapterID}/topics/${topicID}/contentPreview?${new URLSearchParams(
                          __linkParams
                        ).toString()}`}
                      >
                        {`${userInfo?.data?.key}`.toLowerCase() === 'SUBJECT_EXPERT'.toLowerCase()
                          ? `Respond/View comments`
                          : `Add/View comments`}
                      </Link>
                    )}
                  </>
                )}
              <Link
                to={`/classes/${classID}/courses/${courseID}/chapters/${chapterID}/topics/${topicID}/commentHistory`}
                target={'_blank'}
                className="btn-right-content mx-2"
              >
                Review History
                <img src={AppPics.ReviewHistory} className="_sv_ico" alt="" />
                {/*    <Tooltip title="Comments">
               
                  <IconButton color="primary" aria-label="Comments section">
                    <AiOutlineInfoCircle />
                  </IconButton> 
                </Tooltip> */}
              </Link>
              <a
                href={`${process.env.REACT_APP_PREVIEW_URL.replace(/\/$/, '')}/sneak-peak?course=${courseID}&chapter=${chapterID}&lesson=${topicID}&subtopic=${subtopicID}`}
                role={'button'}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-plane g--tint ml-4 d-flex align-items-center"
              >
                {' '}
                Live Preview <MdOutlinePreview className="mx-2" size={24} />
              </a>
            </div>
            {userPermissions?.topic?.notes?.showDraft && (
              <div className="custom-control custom-checkbox draftcheckbox">
                <input
                  type="checkbox"
                  name="notesStatus"
                  className="custom-control-input"
                  id="notesStatus"
                  checked={status === 'draft'}
                  onChange={() => setStatus(status === 'draft' ? 'inactive' : 'draft')}
                />
                <label className="custom-control-label" htmlFor="notesStatus">
                  Draft
                </label>
              </div>
            )}
          </div>
        </div>

        {!subtopicID && (
          <SetTime
            handleTime={(data = {}) => handleSetTime((x) => ({ ...x, ...data }))}
            {...setTime}
          />
        )}
        <div className="row Texteditorsection relative">
          {blocks.map((block) => {
            return (
              <Fragment key={block.id}>
                {['text', 'problem', 'tip', 'box'].includes(block.currentBlock) && (
                  <div className="col-12 block__mar_arrng">
                    <div className="relative">
                      <Editor
                        data={{
                          value: DOMPurify.sanitize(block.value || ''),
                          onChange: (data) => {
                            updateCurrentEditBlock({ id: block.id, data });
                            // setBlocks(
                            //   blocks.map((x) => {
                            //     if (x.id === block.id) return { ...x, value: data };
                            //     return x;
                            //   })
                            // );
                          },
                        }}
                      />
                      {!block.editBlock && (
                        <>
                          <div className="____edit___blocks"></div>
                        </>
                      )}
                    </div>

                    <div className="___icon_label_button">
                      {block.editBlock && !['text'].includes(block.currentBlock) && (
                        <>
                          <input
                            placeholder="Add caption here..."
                            style={{ width: '50%' }}
                            value={block.caption || ''}
                            onChange={(event) => {
                              const caption = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.caption = caption;
                                  }

                                  return x;
                                })
                              );
                            }}
                          />
                        </>
                      )}
                      {block.editBlock && ['problem'].includes(block.currentBlock) && (
                        <>
                          <input
                            placeholder="Add exam name..."
                            className="mx-2"
                            style={{ width: '30%' }}
                            value={block.problemFrom || ''}
                            onChange={(event) => {
                              const problemFrom = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.problemFrom = problemFrom;
                                  }

                                  return x;
                                })
                              );
                            }}
                          />
                          <input
                            placeholder="Add exam year..."
                            className="mx-2"
                            style={{ width: '30%' }}
                            value={block.problemYear || ''}
                            onChange={(event) => {
                              const problemYear = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.problemYear = problemYear;
                                  }

                                  return x;
                                })
                              );
                            }}
                          />
                        </>
                      )}
                      {canSave() && <BlockEditBtn block={block} enableEdit={enableEditing} />}
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {/* <AddBlock block={block} />design issue */}
                      <PreviewBtn label={'Preview Block'} labelIcon={<IoIosEye />} block={block} />
                    </div>
                  </div>
                )}
                {block.currentBlock === 'image' && (
                  <>
                    <div className="position-relative __image_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                      <div className="ratio ratio-21x9">
                        <img src={block.assetUrl} alt="presentational block content" />
                      </div>
                      <div>
                        <>
                          <input
                            placeholder="Add caption here..."
                            style={{ width: '100%' }}
                            value={block.caption || ''}
                            disabled={block.captionDisabled}
                            onChange={(event) => {
                              const caption = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.caption = caption;
                                  }
                                  return x;
                                })
                              );
                            }}
                          />
                        </>
                      </div>

                      {/* <div
                        className="position-absolute __enlargeUImage __pinkColor"
                        role="button"
                        onClick={() =>
                          setImgPreviewModal({ isOpen: true, data: { imageUrl: block.assetUrl } })
                        }
                      >
                        <ImEnlarge2 className="mx-2" size={12} /> <span>Enlarge Image</span>
                      </div> */}
                    </div>
                    <div className="___icon_label_button">
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'image', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.captionDisabled = !x.captionDisabled;
                                }
                                return x;
                              })
                            );
                          }}
                        >
                          <BsPencil />
                          {block.captionDisabled ? `Edit Caption` : `Done Editing`}
                        </AppButton>
                      )}
                      <AppButton
                        variantClass="__enlargeUImage __pinkColor"
                        onClick={() => {
                          setImgPreviewModal({ isOpen: true, data: { imageUrl: block.assetUrl } });
                        }}
                      >
                        <ImEnlarge2 className="mx-2" size={12} /> <span>Enlarge Image</span>
                      </AppButton>
                      {canSave() && <BlockDeleteBtn block={block} />}
                    </div>
                  </>
                )}
                {block.currentBlock === 'video' && (
                  <div className="__video_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="ratio ratio-21x9">
                      <TopicVideoTag block={block} />
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'video', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'audio' && (
                  <div className="__audio_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="ratio ratio-21x9">
                      <TopicAudioTag block={block} />
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'audio', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'file' && (
                  <div className="__file_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="card border-dark mb-3" style={{ width: '18rem' }}>
                      <a href={block.assetUrl} target="_blank" rel="noopener noreferrer">
                        <img src={AppPics.FileSvg} className="card-img-top" alt="" />
                        <p className="h6 text-center my-2">Click to open in a new tab</p>
                      </a>
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'file', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'equation' && (
                  <div className="__equation_tool col-12">
                    <EquationBox
                      equation={DOMPurify.sanitize(block.value)}
                      showProcessButton={false}
                      setEquation={(value) => {
                        setBlocks(
                          blocks.map((x) => {
                            if (x.id === block.id) return { ...x, value };
                            return x;
                          })
                        );
                      }}
                    />
                    <div className="___icon_label_button">
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {/* <AddBlock block={block} /> */}
                      <PreviewBtn block={block} />
                      {/* <PreviewBtn block={block} /> */}
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
          {canSave() && (
            <div className="___placeBlockBuilder">
              <AppBlocks
                handleChange={(currentBlock) => {
                  if (['video', 'image', 'audio', 'file'].includes(currentBlock)) {
                    toggleUploadModal({ currentBlock });
                    return;
                  }
                  setBlocks(
                    blocks.concat({
                      ...blockMasterSchema,
                      id: getRandomUid(),
                      currentBlock,
                    })
                  );
                }}
                reorderSettings={{
                  showReorderBlock: (isAdmin || isSE) && reOrderBlocks?.length > 0,
                  blocks: reOrderBlocks,
                  type: 'NOTE',
                  handleSubmission: (args = {}) => {
                    handleSave({}, args);
                  },
                  checkPrerequisite: () => {
                    const cannotProceed = blocks?.some((b) => isValidUuid(b.id));

                    return { canGoForward: !cannotProceed };
                  },
                }}
              />
            </div>
          )}
        </div>

        <div className="row mt-4">
          <div className="col-6">
            <label>Content Maintainer</label>
            <UserSelect
              dependencies={{ query: { roles: [ROLES.SUBJECT_EXPERT] } }}
              handleChange={setContentMaintainer}
              values={contentMaintainer}
              isMulti={false}
            />
          </div>
          <div className="col-6">
            <label>Content Author</label>
            <UserSelect
              dependencies={{ query: { roles: [ROLES.SUBJECT_EXPERT] } }}
              handleChange={setContentOwner}
              values={contentOwner}
              isMulti={false}
            />
          </div>
          {canChangeStatus() ? (
            <div className="col-6 mt-4">
              <ReviewContent
                area={userPermissions?.topic?.notes}
                value={status}
                onChange={(value) => setStatus(value)}
                disableList={notes[0]?.nonPermittedTransition || []}
                selectProps={{ disabled: teacherCannotChangeStatus }}
              />
              {teacherCannotChangeStatus && (
                <small className="text-danger">
                  Please give a rating first and then change the assignee status.
                </small>
              )}
            </div>
          ) : (
            <div className="col-6">
              {status === 'draft' && <div>The section is on draft mode.</div>}
              {status === 'review' && <div>The section is pending with Subject expert </div>}
              {status === 'qc_review' && <div>The section is pending with QC review</div>}
              {status === 'teacher_review' && <div>The section is pending for Teacher review</div>}
            </div>
          )}
          {isTeacher && !rating && (
            <div className="col-6 mt-4">
              <TutorRating ratingValue={rating} apiArgs={{ noteID: notes[0]?._id, type: 'note' }} />
            </div>
          )}
          {((isSE && status === 'teacher_review') || isAdmin) && (
            <div className="col-6 mt-4">
              <label>Please pick a teacher</label>
              <UserSelect
                dependencies={{ query: { roles: [ROLES.TUTOR] } }}
                handleChange={setContentTutor}
                values={contentTutor}
                isMulti={false}
              />
            </div>
          )}
        </div>
        <div className="row mt-5 btnsubmitmain __flex-j-c-center">
          {canSave('bypass') && (
            <div className="col-3 btnTab-foot">
              <button
                type="button"
                className="btn-right-content save"
                onClick={() => {
                  if (status === 'teacher_review' && !contentTutor) {
                    window.alert('Please pick a teacher');
                    return;
                  }
                  if (isTeacher && teacherCannotChangeStatus) {
                    window.alert('Forgot to add rating?');
                    return;
                  }
                  handleSave();
                }}
              >
                Save Changes
              </button>
            </div>
          )}
          {/* <div className="col-3 btnTab-foot">
            <Link
              to={{
                pathname: `${process.env.REACT_APP_PREVIEW_URL}/sneak-peak?course=${courseID}&chapter=${chapterID}&lesson=${topicID}&subtopic=${subtopicID}`,
                // pathname: `${process.env.REACT_APP_PREVIEW_URL}?course=${courseID}&chapter=${chapterID}&lesson=${topicID}&subtopic=${subtopicID}`,
              }}
              role={'button'}
              target="_blank"
              className="btn-plane g--tint"
              // onClick={(e) => {
              //   e.preventDefault();
              //   window.open(
              //     `${process.env.REACT_APP_PREVIEW_URL}?course=${courseID}&chapter=${chapterID}&lesson=${topicID}&subtopic=${subtopicID}`,
              //     'edpodia_preview'
              //   );
              // }}
            >
              {' '}
              Live Preview{' '}
            </Link>
          </div> */}
          {isTeacher && rating && (
            <div className="col-3 btnTab-foot">
              <button
                type="button"
                className="btn-plane p--tint m-l-btn"
                onClick={() => {
                  setUpdatingRatingModal({
                    show: true,
                    data: {
                      currentRating: rating,
                      apiArgs: {
                        noteID: notes[0]?._id,
                        type: 'note',
                      },
                    },
                  });
                }}
              >
                Change rating
              </button>
            </div>
          )}
          {/* <div>
            {(isAdmin || isSE) && reOrderBlocks?.length > 0 && (
              <RearrangeBlocks
                blocks={reOrderBlocks}
                type="NOTE"
                handleSubmission={(args = {}) => {
                  handleSave({}, args);
                }}
                checkPrerequisite={() => {
                  const cannotProceed = blocks?.some((b) => isValidUuid(b.id));

                  return { canGoForward: !cannotProceed };
                }}
              />
            )}
          </div> */}
        </div>
      </div>
    </>
  );
}

const manageNotesSchema = Joi.object({
  status: Joi.string().required().trim(),
  blocks: Joi.array().required(),
});

function serializeNoteBlocks(blocks = []) {
  return blocks.map(({ editBlock, captionDisabled, id, ...block }, index) => ({
    ...block,
    sortPosition: block.sortPosition ?? index,
  }));
}

function TopicAudioTag({ block }) {
  const audioRef = useRef(null);

  useEffect(() => {
    if (block?.assetUrl) audioRef.current.load();
  }, [block?.assetUrl]);

  return (
    <audio controls ref={audioRef}>
      <source src={block.assetUrl} />
      Your browser does not support the audio tag.
    </audio>
  );
}

function TopicVideoTag({ block }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (block?.assetUrl) videoRef.current.load();
  }, [block?.assetUrl]);

  return (
    <video width="320" height="240" controls ref={videoRef}>
      <source src={block.assetUrl} />
      Your browser does not support the video tag.
    </video>
  );
}

export { TopicVideoTag, TopicAudioTag };
export default TopicNotesTab;
